import React, { Component } from 'react';
import PropTypes from 'prop-types';
import simplify from 'simplify-js';
import classnames from 'classnames';

import TextField from 'material-ui/TextField';

import config from '../../config';

import styles from './Page.css';

class Page extends Component {
    static propTypes = {
        _id: PropTypes.string,
        file: PropTypes.string,
        rotation: PropTypes.number,
        comments: PropTypes.array,
        tool: PropTypes.string,
        onCreateComment: PropTypes.func,
    };

    state = {
        currentPath: null,
        currentPathStyle: null,
        aspectRatio: 1,
        imageLoaded: false,
    };

    componentWillReceiveProps(nextProps) {
        if (nextProps.file !== this.props.file) {
            this.setState({ imageLoaded: false });
        }

        if (nextProps.file !== this.props.file || nextProps.tool !== this.props.tool) {
            this.handleSvgMouseUp();
        }
    }

    handleSvgClick = event => {
        const { tool } = this.props;

        if (tool === 'eraser' && event.target.tagName.toLowerCase() === 'polyline') {
            const { id } = event.target.dataset;

            this.props.onDeleteComment(id);
        }

        if (tool === 'text' && !this.state.currentTextStyle) {
            let { x, y } = this.getCoordinates(event);

            x = x / this.state.aspectRatio;
            this.setState({
                currentText: '',
                currentTextStyle: {
                    left: x + '%',
                    top: y + '%',
                },
            });
        }
    };

    handleSvgMouseDown = event => {
        const { tool } = this.props;

        if (!['pencil', 'highlighter'].includes(tool)) {
            return;
        }

        const { x, y } = this.getCoordinates(event);

        this.setState({
            currentPath: `${x.toFixed(3)},${y.toFixed(3)}`,
            currentPathStyle:
                tool === 'pencil'
                    ? { strokeWidth: '0.25px', stroke: 'red' }
                    : { strokeWidth: '1.75px', stroke: 'rgba(240, 240, 16, 0.5)' },
        });

        this.lastPoint = {
            x,
            y,
            time: performance.now(),
        };
    };

    handleSvgMouseMove = event => {
        if (this.state.currentPath) {
            const { x, y } = this.getCoordinates(event);
            const time = performance.now();
            const { x: lastX, y: lastY, time: lastTime } = this.lastPoint;

            if (lastTime - time < 20 && (x - lastX) ** 2 + (y - lastY) ** 2 < 0.05) {
                return;
            }

            this.setState({
                currentPath: this.state.currentPath + ` ${x.toFixed(3)},${y.toFixed(3)}`,
            });

            this.lastPoint = { x, y, time };
        }
    };

    handleSvgMouseUp = () => {
        if (this.state.currentPath) {
            const path = simplify(
                this.state.currentPath.split(' ').map(pair => {
                    const [x, y] = pair.split(',').map(str => +str);

                    return { x, y };
                }),
                0.1,
                true
            );

            this.props
                .onCreateComment({
                    path: path.map(({ x, y }) => `${x},${y}`).join(' '),
                    style: this.state.currentPathStyle,
                })
                .then(() => {
                    this.setState({
                        currentPath: null,
                        currentPathStyle: null,
                    });
                });
        }
    };

    handleSaveTextComment = () => {
        this.props
            .onCreateComment({
                text: this.state.currentText,
                style: this.state.currentTextStyle,
            })
            .then(() => {
                this.setState({
                    currentText: null,
                    currentTextStyle: null,
                });
            });
    };

    handleImageLoad = event => {
        const { naturalWidth, naturalHeight } = event.currentTarget;

        this.setState({ aspectRatio: naturalWidth / naturalHeight, imageLoaded: true });
    };

    handleDeleteClick = event => {
        if (this.props.tool === 'eraser') {
            const { id } = event.currentTarget.dataset;

            this.props.onDeleteComment(id);
        }
    };

    getCoordinates = event => {
        const bbox = event.currentTarget.getBoundingClientRect();

        return {
            x: ((event.clientX - bbox.left) / bbox.width) * 100 * this.state.aspectRatio,
            y: ((event.clientY - bbox.top) / bbox.height) * 100,
        };
    };

    render() {
        const { file, rotation, comments = [], tool, showComments } = this.props;
        const { currentPath, currentPathStyle, aspectRatio, currentText, currentTextStyle, imageLoaded } = this.state;

        const rotated = Math.abs(((rotation || 0) / 90) % 2) === 1;
        let width, height;

        if (rotated) {
            width = aspectRatio >= 1 ? 65 : 90;
            height = width * aspectRatio;
        } else {
            width = aspectRatio >= 1 ? 90 : 65;
            height = width / aspectRatio;
        }

        return (
            <div
                className={classnames(styles.page, {
                    [styles.landscape]: aspectRatio >= 1,
                    [styles.portrait]: aspectRatio < 1,
                    [styles.rotated]: rotated,
                    [styles.hasTool]: ['pencil', 'highlighter'].includes(tool),
                })}
                style={{
                    width: `${width}vw`,
                    height: `${height}vw`,
                }}
            >
                <img
                    src={`${config.backend}/uploads/${file}`}
                    onLoad={this.handleImageLoad}
                    style={{
                        transform: `translate(-50%, -50%) rotate(${rotation || 0}deg)`,
                    }}
                    alt="Page content"
                />
                <svg
                    onMouseDown={this.handleSvgMouseDown}
                    onMouseMove={this.handleSvgMouseMove}
                    onMouseUp={this.handleSvgMouseUp}
                    onMouseLeave={this.handleSvgMouseUp}
                    onClick={this.handleSvgClick}
                    viewBox={`0 0 ${100 * aspectRatio} 100`}
                    preserveAspectRatio="none"
                >
                    {imageLoaded &&
                        showComments &&
                        comments
                            .filter(({ path }) => path)
                            .map(({ _id, path, style }) => pointsToPolyline(path, _id, style))}
                    {currentPath && pointsToPolyline(currentPath, 'current', currentPathStyle)}
                </svg>
                {imageLoaded &&
                    showComments &&
                    comments.filter(({ text }) => text).map(({ _id, text, style }) => (
                        <div
                            data-id={_id}
                            onClick={this.handleDeleteClick}
                            key={_id}
                            style={style}
                            className={classnames(styles.textComment, styles.saved)}
                        >
                            {text}
                        </div>
                    ))}
                {currentTextStyle && (
                    <div style={currentTextStyle} className={classnames(styles.textComment)}>
                        <div className={styles.clickAway} onClick={this.handleSaveTextComment} />
                        <TextField
                            autoFocus
                            value={currentText}
                            floatingLabelText="Comment"
                            multiLine
                            floatingLabelStyle={{ display: 'none' }}
                            textareaStyle={{ margin: 0 }}
                            underlineShow={false}
                            onChange={(e, currentText) => this.setState({ currentText })}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default Page;

function pointsToPolyline(points, key, style) {
    return <polyline key={key} fill="none" style={style} points={points} data-id={key} />;
}



// WEBPACK FOOTER //
// ./src/Components/Paper/Page.js